import type {
  Product,
  SkuSpecificationsName,
} from 'src/components/restructure/product/types/product'
import { KEYWORDS_HIDDEN_COMPONENT } from 'src/components/restructure/product/sections/Sidebar/SkuSelector/DefaultSelector'
import { ChevronDown } from 'src/components/Icons/ChevronDown'
import { useRef, useState } from 'react'
import { ChevronUp } from 'src/components/Icons/ChevronUp'
import { useClickAway } from 'react-use'

const TITLE_MAP = {
  Cor: 'Selecione a cor',
  Sabor: 'Selecione o sabor',
  Tamanho: 'Selecione o tamanho',
  Valor: 'Selecione o valor',
  Voltagem: 'Selecione a voltagem',
}

const SimpleProductCardSelector = ({
  product,
  filters = [],
  onSelect,
  selectedSku,
}: {
  product: Product
  filters?: string[]
  onSelect: React.Dispatch<React.SetStateAction<string | null>>
  selectedSku?: string | null
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { items } = product
  const ref = useRef(null)

  useClickAway(ref, () => isOpen && setIsOpen(false))

  if (items.filter((item) => item.isAvailable).length === 1) {
    onSelect(items[0].itemId)

    return null
  }

  const availableSpecifications = items.reduce((acc, cur) => {
    if (!cur.isAvailable) {
      return acc
    }

    cur.variations.forEach((variation) => {
      const variationName = variation as SkuSpecificationsName

      if (filters.includes(variationName)) {
        return
      }

      const values = cur[variationName]

      if (!values || KEYWORDS_HIDDEN_COMPONENT.includes(values[0])) {
        return
      }

      if (!acc[variationName]) {
        acc[variationName] = []
      }

      const position = product.skuSpecifications
        .find((specification) => specification?.field?.name === variation)
        ?.values?.find((value) => value?.name === values?.[0])?.position

      acc[variationName].push({
        value: values[0],
        skuId: cur.itemId,
        position,
      })
    })

    return acc
  }, {} as Record<SkuSpecificationsName, Array<{ value: string; skuId: string; position: number | undefined }>>)

  return (
    <div className="mt-4 px-4">
      {Object.keys(availableSpecifications).map((specification) => {
        const skuTitle = items
          ?.find((item) => item.itemId === selectedSku)
          ?.[specification as SkuSpecificationsName]?.[0]?.trim()

        const specificationTitle =
          TITLE_MAP[specification as keyof typeof TITLE_MAP] ?? specification

        return (
          <div
            className="flex flex-col bg-mediumGray pl-4 pr-2 relative"
            key={`cross-sell-${specification}`}
            ref={ref}
          >
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex justify-between items-center  w-full h-10"
            >
              <p className="text-xs text-restructure-primary font-normal">
                {skuTitle && skuTitle?.length > 0
                  ? skuTitle
                  : specificationTitle}
              </p>

              {isOpen ? <ChevronUp /> : <ChevronDown />}
            </button>

            {isOpen && (
              <div className="pr-[6px] absolute z-[100] bottom-0 top-full left-0 w-full h-fit bg-mediumGray">
                <ul className="px-4 sm:max-h-[210px] overflow-auto flex flex-col">
                  {availableSpecifications[
                    specification as SkuSpecificationsName
                  ]?.map(({ value, skuId, position }) => (
                    <li
                      key={`cross-sell-select-${skuId}-${value}`}
                      style={{ order: position }}
                      className="last:pb-2"
                    >
                      <button
                        className="flex w-full items-center text-sm text-restructure-primary font-normal min-h-6 py-3 border-t border-t-gray"
                        onClick={() => {
                          onSelect(skuId)
                          setIsOpen(false)
                        }}
                      >
                        {value}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default SimpleProductCardSelector
